import { TimelineModel } from '@mpt-fo/common/shared/models/timeline.model';
import { ProfileService } from '@mpt-fo/common/core/services/profile.service';
import { StringHelper } from '@mpt-fo/common/core/helpers/strings.helper';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Component, OnInit } from '@angular/core';
import { AuthenticationService } from '@mpt-fo/common/core/services/authentication.service';
import { MessageModalComponent } from '../login/partials/message-modal/message-modal.component';
import { ModalOptionsConfig, ModalService } from '@mpt-fo/common/lean-shared/components/nz-modal-service/modal.service';
import { MessageType } from '@mpt-fo/common/shared/models/utility.model';
import { ScreenManagerService } from '@mpt-fo/common/core/services/screen-manager.service';
import { Router } from '@angular/router';
import { DeviceInformation } from '@mpt-fo/common/shared/models/device.model';
import { DeviceHelperService } from '@mpt-fo/common/core/services/device-helper.service';
import { DataLayerService } from '@mpt-fo/common/core/services/data-layer-service.service';

@Component({
	selector: 'app-forgot-username',
	templateUrl: './forgot-username.component.html',
	styleUrls: ['./forgot-username.component.scss'],
})
export class ForgotUsernameComponent implements OnInit {
	stepsArray: TimelineModel[];
	currentStep: TimelineModel;
	currentStepIndex: number;
	finalStepIndex: number;

	usernameForm!: FormGroup;
	submitting: boolean;
	showSuccess: boolean;
	errorMessage: string;
	showLivelinessCheck: boolean;
	livelinessObj: { files; pin };
	username: any;
	submittingForm: boolean;
	identifiers = ['ACCOUNT_NUMBER', 'TERMINAL_ID', 'SERIAL_NO'];
	otpError: string;
	deadline: number;
	countDown: boolean;
	timer: number;
	countDownTimer: any;
	wait: boolean;
	steps = steps;
	stepIndex: number;
	loadingUssd: boolean;
	res: { ussdCode };
	runningModal: any;
	ROUTE = '/forgot-username';
	deviceInformation: DeviceInformation

	constructor(
		private fb: FormBuilder,
		private auth: AuthenticationService,
		private profileService: ProfileService,
		private modalService: ModalService,
		private screenManager: ScreenManagerService,
		private router: Router,
		private dataLayerService: DataLayerService,
		private deviceHelper: DeviceHelperService
	) {
		this.usernameForm = this.fb.group({
			userIdentifier: [null, [Validators.required]],
			userIdentifierType: [null, [Validators.required]],
			pin: [null, [Validators.required]],
			bvn: [null, [Validators.required]],
			otp: [null, [Validators.required]],
		});
	}

	ngOnInit(): void {
		this.deviceInformation = this.deviceHelper.getDeviceInformation();
		if (this.screenManager.isMobile()) {
			this.routeToMobile();
		}
		this.screenManager.isMobile$.subscribe((isMobile) => {
			if (isMobile && this.router?.url === this.ROUTE) {
				this.routeToMobile();
			}
		});
		const payload = this.saveLogs('sign_in_forgot_username_initiated', '', '')
		this.dataLayerService.logEvent(payload);
		this.getUsernameUssd();
	}

	routeToMobile() {
		this.router.navigateByUrl('mobile/recovery/username');
	}

	getUsernameUssd() {
		this.loadingUssd = true;
		this.auth
			.recoverUsernameUssd()
			.then((res) => {
				this.res = res;
				this.loadingUssd = false;
				this.showSuccess = true;
			})
			.catch((err) => {
				const description = StringHelper.getErrorMessageHttpErrorResponse(err) || 'An Error occurred';
				const params = {
					title: 'Request Failed',
					description,
					messageType: MessageType.ERROR,
					buttonText: 'Retry',
					canShowCancelButton: true,
				};
				this.launchMessageModal(params);
				this.loadingUssd = false;
			});
	}

	launchMessageModal(params) {
		const config = this.createModalConfig(params);
		this.runningModal = this.modalService.createModal(MessageModalComponent, config);
	}

	createModalConfig(params) {
		const config = new ModalOptionsConfig();
		config.nzComponentParams.data = params;
		config.nzComponentParams.callback = (result) => {
			this.handleOncloseMessageModal();
		};
		return config;
	}

	handleOncloseMessageModal() {
		this.runningModal?.destroy();
		this.getUsernameUssd();
	}

	triggerOtp() {
		this.otpError = null;
		const data = this.buildOtpRequest();
		this.auth
			.triggerOtpForUsernameRetrieval(data)
			.then((res) => {
				this.setTimerValue();
				this.deadline = Date.now() + 1000 * 60 + 1000 * 1;
				this.changeStep();
			})
			.catch((err) => {
				this.setTimerValue();
				this.otpError = StringHelper.getErrorMessageHttpErrorResponse(err);
				this.countDown = false;
			});
	}

	buildOtpRequest() {
		const form = this.usernameForm.value;
		const data = {
			userIdentifier: form.userIdentifier,
			userIdentifierType: form.userIdentifierType,
		};
		return data;
	}

	continue() {
		switch (this.stepIndex) {
			case 0:
				if (this.usernameForm.controls.userIdentifier.invalid || this.usernameForm.controls.userIdentifierType.invalid) {
					this.usernameForm.controls.userIdentifier.markAsDirty();
					this.usernameForm.controls.userIdentifierType.markAsDirty();
					this.usernameForm.controls.userIdentifier.updateValueAndValidity();
					this.usernameForm.controls.userIdentifierType.updateValueAndValidity();
					return;
				}
				this.triggerOtp();
				break;
			case 1:
				for (const i in this.usernameForm.controls) {
					if (this.usernameForm && this.usernameForm.controls) {
						this.usernameForm.controls[i].markAsDirty();
						this.usernameForm.controls[i].updateValueAndValidity();
					}
				}
				if (this.usernameForm.invalid) {
					return;
				}
				this.validatePin();
				break;
			default:
				break;
		}
	}

	validatePin() {
		this.submittingForm = true;
		const form = this.usernameForm.value;
		const param = {
			pin: form.pin,
			userIdentifierType: form.userIdentifierType,
			userIdentifier: form.userIdentifier,
		};
		this.profileService
			.valideUsernamePin(param)
			.then((res) => {
				this.showLivelinessCheck = true;
				this.submittingForm = false;
			})
			.catch((err) => {
				this.errorMessage = StringHelper.getErrorMessageHttpErrorResponse(err);
				this.submittingForm = false;
			});
	}

	cancel() {
		this.showLivelinessCheck = false;
	}

	changeStep() {
		if (this.stepIndex >= this.steps.length - 1) {
			return;
		}
		this.stepIndex++;
		this.currentStep = this.stepsArray[this.stepIndex];
	}

	submitForm(shots) {
		this.livelinessObj = shots;
		this.showLivelinessCheck = false;
		this.errorMessage = null;
		this.submitting = true;
		const params = this.buildParams();
		this.profileService
			.retrieveUsernameWithPhotos(params)
			.then((res) => {
				this.username = res.username;
				this.submitting = false;
				this.showSuccess = true;
			})
			.catch((err) => {
				this.submitting = false;
				this.errorMessage = StringHelper.getErrorMessageHttpErrorResponse(err);
			});
	}

	buildParams() {
		const params = {
			userIdentifier: this.usernameForm.value.userIdentifier,
			bvn: this.usernameForm.value.bvn,
			userIdentifierType: this.usernameForm.value.userIdentifierType,
			imageOne: this.livelinessObj.files[0],
			imageTwo: this.livelinessObj.files[1],
			pin: this.usernameForm.value.pin,
			otp: this.usernameForm.value.otp,
		};
		return params;
	}

	setTimerValue() {
		this.wait = false;
		this.countDown = true;
		this.countDownTimer = setInterval(() => {
			this.timer--;
			if (this.timer === 0) {
				this.countDown = false;
				clearInterval(this.countDownTimer);
				this.timer = 60;
			}
		}, 1000);
	}

	private initializeStep() {
		this.currentStepIndex = 0;
		this.finalStepIndex = this.stepsArray.length - 1;
		this.currentStep = this.stepsArray[this.currentStepIndex];
	}

	private createSteps() {
		this.stepsArray = [
			{
				state: 'active',
				number: 1,
				title: 'Create your account!',
				description: 'Enter your details to get started',
			},
			{
				state: 'active',
				number: 2,
				title: 'Verify your phone number',
				description: `We’ve just sent a 6 digit code to your phone
				Check your messages and enter it here`,
			},
			{
				state: 'inactive',
				number: 3,
				title: 'Create your account!',
				description: 'Enter your BVN details',
			},
		];
	}
	saveLogs(event, username, message = '') {
		const payload = new LoginEventLog(event);
		payload.device_type = this.deviceInformation?.model?.toString()?.toUpperCase();
		payload.device_id = this.deviceInformation?.deviceUniqueIdentifier;
		payload.error_message = message;
		payload.user_name = username;
		return payload;
	}
}

const steps = [
	{
		number: 1,
		description: 'Identifier',
	},
	{
		number: 2,
		description: 'Otp',
	},
];

export class LoginEventLog {
	event: string;
	step: string;
	'device_type': string;
	'device_id': string;
	'error_message': string;
	'onboarding_mode': string;
	'user_name': string;

	constructor(event) {
		this.event = event;
	}
}